import qs from 'qs';

import type { PlainQueryParams } from '../interfaces/algolia';

const ENCODED_AMPERSAND = encodeURIComponent('&');
const ENCODED_PLUS = encodeURIComponent('+');

export const replaceSpacing = (
  input: string | number,
  replaceValue = '+',
): string => `${input}`.replace(/ +|%20+/g, replaceValue);

const encodeAmpersand = (
  input: string | number,
  replaceValue = ENCODED_AMPERSAND,
): string => `${input}`.replace(/ +|&+/g, replaceValue);

const encodePlus = (
  input: string | number,
  replaceValue = ENCODED_PLUS,
): string => `${input}`.replace(/\+/g, replaceValue);

export const stringify = (
  queryParams: PlainQueryParams,
  addQueryPrefix = true,
): string =>
  qs.stringify(queryParams, {
    addQueryPrefix,
    encoder: (value, defaultEncoder, charset, type) => {
      let encodedValue = value;
      if (type === 'value') {
        encodedValue = encodePlus(encodedValue);
        encodedValue = replaceSpacing(encodedValue);
        encodedValue = encodeAmpersand(encodedValue);
      }

      return encodedValue;
    },
  });

export const replacePlusOperatorBySpace = (value: string) =>
  value.replace(/\+/g, ' ');
export const replaceMinusOperatorBySpace = (value: string) =>
  value.replace(/-/g, ' ');
export const replaceEncodedPlusOperatorBySpace = (value: string) =>
  value.replace(/%2B/g, ' ');
export const decodePlusOperator = (value: string) =>
  value.replace(/ +|%20+/g, '+');
export const decodePlus = (
  input: string | number,
  replaceValue = '+',
): string => `${input}`.replace(/%2B/g, replaceValue);

/**
 *  Made for SEO purposes, we want to have a clean URL,
 *  so after encoding it we replace space encoding to make it more readable.
 *
 *  Encoding for RFC3986, the more recent RFC3986 makes square brackets
 *  reserved (for IPv6) and thus not encoded when forming something which
 *  could be part of a URL (such as a host).
 *  source: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURI#encoding_for_rfc3986
 *
 *  Unescaped characters: ,=&$:@;/?#[]
 *  Warning: Do not use encodeURIComponent/decodeURIComponent here, as the above characters
 *  are automatically decoded in ParsedUrlQuery from node:querystring.
 */
export const customEncodeURI = (query: string) =>
  replaceSpacing(encodeURI(query).replace(/%5B/g, '[').replace(/%5D/g, ']'));
export const sanitizeQuery = (query: string) => {
  try {
    // NOTE Manually decoded as not managed by decodeURI
    const additionalDecodedChars = query
      ?.replace(/%2B/g, '+')
      .replace(/%26/g, '&');
    return decodeURI(additionalDecodedChars).replace(/\+/g, ' ').trim();
  } catch (error) {
    error.message = `Error sanitizing query: ${query}; ${error.message}`;
    throw error;
  }
};
